import React from 'react'
import { useIntl } from 'react-intl'
import {
  useRecoilCallback,
  useRecoilValueLoadable,
  useSetRecoilState
} from 'recoil'
import {
  TOAST_VARIANT_WARNING,
  toastShow,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Text,
  TEXT_VARIANT_LIGHT,
  SkeletonLoader,
  SKELETON_LOADER_VARIANT_ONWHITE
} from 'mailup-web-components'
import {
  COLLABORATION_PROJECT_STATUS_APPROVED,
  COLLABORATION_PROJECT_STATUS_ARCHIVED,
  COLLABORATION_PROJECT_STATUS_OPEN,
} from 'Constants/'
import { getCollaborationLoginDataCallback } from 'Pages/MessageList/recoil/callbacks/callbacks'
import {
  collaborationState,
  messagesCollaborationState
} from 'Pages/MessageList/recoil/state/results'
import { modalBuyCollaborationState } from 'Pages/MessageList/recoil/state/page'
import './ResultCollaboration.scss'
import { getLang } from 'mailup-react-helpers'

const ResultCollaboration = ({
  collaborationEnabled,
  buyCollaboration,
  Id,
  Subject,
  loading
}) => {
  const intl = useIntl()

  const setModalBuy = useSetRecoilState(modalBuyCollaborationState)
  const setCollaborationState = useSetRecoilState(collaborationState)

  const getCollaborationLoginData = useRecoilCallback(
    getCollaborationLoginDataCallback,
    []
  )

  const collaborationResult = collaborationEnabled
    ? useRecoilValueLoadable(messagesCollaborationState)
    : undefined

  const collaborationInfo =
    collaborationResult && collaborationResult.state === 'hasValue'
      ? collaborationResult.contents.Items.find(
          o => o.ProjectReference === `NL_${Id}`
        )
      : undefined

  const handleClickBuy = () => {
    setModalBuy(true)
  }

  const openCollaboration = async hideResolvedThreads => {
    const result = await getCollaborationLoginData(
      Id,
      collaborationInfo ? collaborationInfo.IdProject : undefined,
      Subject,
      hideResolvedThreads
    )
    if (result) {
      const lang = getLang()
      const loginUrl = `https://${process.env.COLLABORATION_FRONTEND_URL}/login?token=${result.Data.Token}&lang=${lang}`

      window.open(loginUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => {
        setCollaborationState(prev => [...prev])
      }, 2000)
    } else {
      toastShow(
        intl.formatMessage({
          id: 'Load.ActiveRecipientSummary.CollaborationErrorLoadingData'
        }),
        TOAST_VARIANT_WARNING,
        'collaboration'
      )
    }
  }

  const handleOpenCollaboration = () => openCollaboration(true)

  const handleOpenCollaborationNoThreads = () => openCollaboration(false)

  if (
    loading ||
    (collaborationResult && collaborationResult.state === 'loading')
  ) {
    return (
      <div className='c-collaboration'>
        <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
      </div>
    )
  }

  if (collaborationResult && collaborationResult.state === 'hasError') {
    return (
      <div className='c-collaboration'>
        <ul>
          <li className='c-collaboration__row'>
            <Link
              variant={LINK_VARIANT_HIGHLIGHT}
              className='c-collaboration__comments test-openCollaboration'
              onClick={handleOpenCollaboration}
            >
              0
            </Link>
            <Text variant={TEXT_VARIANT_LIGHT}>
              {intl.formatMessage({
                id: 'Load.Collaboration.CollaborationColumnTitle'
              })}
            </Text>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <div className='c-collaboration'>
      <ul>
        {buyCollaboration && (
          <li className='c-collaboration__row'>
            <Link
              variant={LINK_VARIANT_HIGHLIGHT}
              className='c-collaboration__buy openModalBuyCollaboration'
              onClick={handleClickBuy}
            >
              {intl.formatMessage({
                id: 'Load.Collaboration.StartCollaborationCta'
              })}
            </Link>
          </li>
        )}

        {collaborationEnabled && !collaborationInfo && (
          <li className='c-collaboration__row'>
            <Link
              variant={LINK_VARIANT_HIGHLIGHT}
              className='c-collaboration__comments test-openCollaboration'
              onClick={handleOpenCollaboration}
            >
              {intl.formatMessage({
                id: 'Load.Collaboration.StartCollaborationCta'
              })}
            </Link>
            <Text
              variant={TEXT_VARIANT_LIGHT}
              className='c-collaboration__note'
            >
              {intl.formatMessage({
                id: 'Load.Collaboration.CollaborationColumnTitle'
              })}
            </Text>
          </li>
        )}

        {collaborationEnabled && collaborationInfo && (
          <>
            {(collaborationInfo.State === COLLABORATION_PROJECT_STATUS_OPEN ||
              collaborationInfo.State ===
                COLLABORATION_PROJECT_STATUS_APPROVED) && (
              <>
                <li className='c-collaboration__row'>
                  <Link
                    variant={LINK_VARIANT_HIGHLIGHT}
                    className='c-collaboration__comments test-openCollaboration'
                    onClick={handleOpenCollaboration}
                    data-mu-test='collaboration-comments'
                  >
                    {intl.formatNumber(collaborationInfo.NumOfComments)}
                  </Link>
                  {(collaborationInfo.State ===
                    COLLABORATION_PROJECT_STATUS_OPEN ||
                    collaborationInfo.State ===
                      COLLABORATION_PROJECT_STATUS_APPROVED) && (
                    <Text
                      variant={TEXT_VARIANT_LIGHT}
                      htmlElement='span'
                      className='c-collaboration__label'
                    >
                      {intl.formatMessage({
                        id: 'Load.Collaboration.TotalComments'
                      })}
                    </Text>
                  )}
                </li>

                <li className='c-collaboration__row'>
                  <Link
                    variant={LINK_VARIANT_HIGHLIGHT}
                    className='c-collaboration__open-threads test-openCollaboration'
                    onClick={handleOpenCollaborationNoThreads}
                    data-mu-test='collaboration-open-threads'
                  >
                    {intl.formatNumber(
                      collaborationInfo.NumOfUnresolvedThreads
                    )}
                  </Link>
                  <Text
                    variant={TEXT_VARIANT_LIGHT}
                    htmlElement='span'
                    className='c-collaboration__label'
                  >
                    {intl.formatMessage({
                      id: 'Load.Collaboration.OpenThreads'
                    })}
                  </Text>
                </li>

                <li className='c-collaboration__row'>
                  <Link
                    variant={LINK_VARIANT_HIGHLIGHT}
                    className='c-collaboration__resolved-threads test-openCollaboration'
                    onClick={handleOpenCollaboration}
                    data-mu-test='collaboration-resolved-threads'
                  >
                    {intl.formatNumber(collaborationInfo.NumOfResolvedThreads)}
                  </Link>
                  <Text
                    variant={TEXT_VARIANT_LIGHT}
                    htmlElement='span'
                    className='c-collaboration__label'
                  >
                    {intl.formatMessage({
                      id: 'Load.Collaboration.ResolvedThreads'
                    })}
                  </Text>
                </li>
              </>
            )}

            {collaborationInfo.State ===
              COLLABORATION_PROJECT_STATUS_ARCHIVED && (
              <li className='c-collaboration__row'>
                <Link
                  variant={LINK_VARIANT_HIGHLIGHT}
                  className='c-collaboration__archived test-openCollaboration'
                  onClick={handleOpenCollaboration}
                >
                  {intl.formatMessage({
                    id: 'Load.Collaboration.CollaborationArchived'
                  })}
                </Link>
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  )
}

export default ResultCollaboration
